import React, {
	useEffect,
	useReducer,
	useState,
	useCallback,
	useContext,
} from "react";

import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import {
	Pencil,
	Trash2,
	Download,
	Plus,
	Play,
	Pause,
	Workflow,
	XCircle,
	Copy,
	AlertTriangle,
	History,
	Loader2,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../../components/ui/tooltip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermissions from "../../hooks/usePermissions";

const STATUS_LABELS = {
	draft: "Não enviada",
	scheduled: "Agendada",
	running: "Enviando agora",
	paused: "Pausada",
	completed: "Terminou",
	cancelled: "Cancelada",
};

// O nome do status não diz se tem mensagem saindo neste momento. Isto diz.
const STATUS_AJUDA = {
	draft: "Nada foi enviado. Só sai quando você clicar em Iniciar.",
	scheduled: "Nada foi enviado ainda.",
	running: "Está mandando mensagens para os contatos agora.",
	paused: "Parou no meio. Quem ainda não recebeu, não recebeu.",
	completed: "Todos os contatos já receberam.",
	cancelled: "Foi interrompida. O resto dos contatos não recebe.",
};

const STATUS_CLASSES = {
	draft: "bg-slate-200 text-slate-700 hover:bg-slate-200",
	scheduled: "bg-blue-100 text-blue-700 hover:bg-blue-100",
	running: "bg-green-100 text-green-700 hover:bg-green-100",
	paused: "bg-yellow-100 text-yellow-700 hover:bg-yellow-100",
	completed: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
	cancelled: "bg-red-100 text-red-700 hover:bg-red-100",
};

// Como o log do backend chama cada coisa, em português.
const ROTULO_DE_EVENTO = {
	info: "Aconteceu",
	start: "Iniciada",
	pause: "Pausada",
	cancel: "Cancelada",
	finish: "Terminada",
	error: "Falhou",
};

const reducer = (state, action) => {
	if (action.type === "LOAD_CAMPAIGNS") {
		const campaigns = action.payload;
		const newCampaigns = [];

		campaigns.forEach((campaign) => {
			const index = state.findIndex((c) => c.id === campaign.id);
			if (index !== -1) {
				state[index] = campaign;
			} else {
				newCampaigns.push(campaign);
			}
		});

		return [...state, ...newCampaigns];
	}

	if (action.type === "UPDATE_CAMPAIGNS") {
		const campaign = action.payload;
		const index = state.findIndex((c) => c.id === campaign.id);

		if (index !== -1) {
			state[index] = { ...state[index], ...campaign };
			return [...state];
		} else {
			return [campaign, ...state];
		}
	}

	if (action.type === "DELETE_CAMPAIGN") {
		const campaignId = action.payload;
		const index = state.findIndex((c) => c.id === campaignId);
		if (index !== -1) {
			state.splice(index, 1);
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

const CampaignStatusBadge = ({ status }) => (
	<Badge
		className={cn(
			"border-transparent",
			STATUS_CLASSES[status] || STATUS_CLASSES.draft
		)}
	>
		{STATUS_LABELS[status] || status}
	</Badge>
);

const Campaigns = () => {
	const { user } = useContext(AuthContext);
	const { temPermissao } = usePermissions();
	// A lista já vem recortada do backend. Aqui o recorte serve para o aviso do
	// socket: sem ele, a campanha que um colega acabou de criar apareceria na
	// tela de quem não pode vê-la, até o próximo F5.
	const vejoTodas = temPermissao("campaigns:viewAll");

	const [campaigns, dispatch] = useReducer(reducer, []);
	const [loading, setLoading] = useState(false);
	const [statsMap, setStatsMap] = useState({});

	const [campaignModalOpen, setCampaignModalOpen] = useState(false);
	const [selectedCampaign, setSelectedCampaign] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	// Iniciar é o único clique desta tela que faz mensagem sair para cliente
	// de verdade; ele não pode ser um clique distraído.
	const [aIniciar, setAIniciar] = useState(null);
	// Histórico: cada início, pausa, repetição e resposta da campanha. Numa
	// campanha que repete, o total somado não conta o que houve em cada rodada.
	const [historicoDe, setHistoricoDe] = useState(null);
	const [historico, setHistorico] = useState([]);
	const [carregandoHistorico, setCarregandoHistorico] = useState(false);

	const abrirHistorico = async (campanha) => {
		setHistoricoDe(campanha);
		setCarregandoHistorico(true);
		try {
			const { data } = await api.get(`/campaigns/${campanha.id}/history`);
			setHistorico(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
		}
		setCarregandoHistorico(false);
	};

	// Baixa o CSV de entrega. Vai pelo `api` e não por um link direto porque a
	// rota exige o token da sessão — um <a href> sairia sem ele e daria 403.
	const baixarRelatorio = async (campaign) => {
		try {
			const { data } = await api.get(
				`/campaigns/${campaign.id}/delivery-report`,
				{ responseType: "blob" }
			);

			const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
			const link = document.createElement("a");
			link.href = url;
			link.download = `campanha-${campaign.id}-entrega.csv`;
			link.click();
			URL.revokeObjectURL(url);
		} catch (err) {
			toastError(err);
		}
	};

	const fetchStats = useCallback(async (campaignId) => {
		try {
			const { data } = await api.get(`/campaigns/${campaignId}/stats`);
			setStatsMap((prev) => ({ ...prev, [campaignId]: data }));
		} catch (err) {
			// silencioso: stats são informativas
		}
	}, []);

	useEffect(() => {
		(async () => {
			setLoading(true);
			try {
				const { data } = await api.get("/campaigns");
				dispatch({ type: "LOAD_CAMPAIGNS", payload: data });
				setLoading(false);
				data.forEach((campaign) => fetchStats(campaign.id));
			} catch (err) {
				toastError(err);
				setLoading(false);
			}
		})();
	}, [fetchStats]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("campaign", (data) => {
			if (data.action === "update" || data.action === "create") {
				const minha = String(data.campaign?.createdBy) === String(user?.id);

				if (!vejoTodas && !minha) return;

				dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.campaign });
				fetchStats(data.campaign.id);
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_CAMPAIGN", payload: data.campaignId });
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [fetchStats, vejoTodas, user]);

	const handleOpenCampaignModal = () => {
		setSelectedCampaign(null);
		setCampaignModalOpen(true);
	};

	const handleCloseCampaignModal = () => {
		setCampaignModalOpen(false);
		setSelectedCampaign(null);
	};

	const handleEditCampaign = (campaign) => {
		setSelectedCampaign(campaign);
		setCampaignModalOpen(true);
	};

	const handleDeleteCampaign = async (campaignId) => {
		try {
			await api.delete(`/campaigns/${campaignId}`);
			toast.success("Campanha excluída com sucesso.");
		} catch (err) {
			toastError(err);
		}
		setSelectedCampaign(null);
	};

	const handleAction = async (campaignId, action, successMessage) => {
		try {
			await api.post(`/campaigns/${campaignId}/${action}`);
			toast.success(successMessage);
		} catch (err) {
			toastError(err);
		}
	};

	const renderStats = (campaignId) => {
		const stats = statsMap[campaignId];
		if (!stats) {
			return <span className="text-xs text-muted-foreground">-</span>;
		}
		// "Saiu" é o que o WhatsApp aceitou; entregue e lida são confirmações
		// que chegam depois, então são subconjuntos de "saiu" — nunca somam
		// com ele.
		return (
			<span className="block text-xs leading-snug text-muted-foreground">
				<span className="block">{stats.sent} saíram</span>
				{stats.delivered > 0 && (
					<span className="block">{stats.delivered} chegaram no aparelho</span>
				)}
				{/* A diferença entre o que saiu e o que foi confirmado não é
				    "não chegou": é envio de que o WhatsApp ainda não devolveu o
				    aviso de entrega, ou de que a ponte não confirmou o id (aí
				    nunca haverá aviso). Sem esta linha, quem olha conclui que
				    as mensagens se perderam — foi a queixa que originou isto. */}
				{stats.sent > stats.delivered && (
					<span className="block">
						{stats.sent - stats.delivered} sem confirmação de entrega
					</span>
				)}
				{stats.read > 0 && <span className="block">{stats.read} leram</span>}
				{/* O número que diz se a campanha funcionou. Entregue e lida só
				    dizem que ela chegou. */}
				{stats.replied > 0 && (
					<span className="block font-medium text-primary">
						{stats.replied} responderam
					</span>
				)}
				{stats.pending > 0 && (
					<span className="block">{stats.pending} ainda vão receber</span>
				)}
				{stats.failed > 0 && (
					<span className="block text-destructive">
						{stats.failed} não saíram
					</span>
				)}
			</span>
		);
	};

	return (
		<MainContainer className="tema-campanha">
			<ConfirmationModal
				title={
					selectedCampaign &&
					`Excluir campanha ${selectedCampaign.name}?`
				}
				open={confirmModalOpen}
				onClose={() => {
					setConfirmModalOpen(false);
					setSelectedCampaign(null);
				}}
				onConfirm={() => handleDeleteCampaign(selectedCampaign.id)}
				confirmLabel="Apagar campanha"
			>
				A campanha some da lista junto com o histórico de quem recebeu. Não
				desfaz mensagens que já foram enviadas, e não dá para voltar atrás.
			</ConfirmationModal>
			<ConfirmationModal
				title={
					aIniciar && `Começar a enviar “${aIniciar.name}” agora?`
				}
				open={!!aIniciar}
				onClose={() => setAIniciar(null)}
				confirmLabel="Sim, começar a enviar"
				onConfirm={() =>
					handleAction(aIniciar.id, "start", "Campanha iniciada.")
				}
			>
				{aIniciar &&
					`${
						aIniciar.flow
							? `O fluxo “${aIniciar.flow.name}” começa a ser enviado`
							: "As mensagens começam a sair"
					} pelo WhatsApp ${
						aIniciar.whatsapp?.name || "da conexão escolhida"
					} para ${
						statsMap[aIniciar.id]?.pending ?? "os"
					} contato(s) de verdade, um a um. Isso não pode ser desfeito: quem já recebeu, recebeu. Dá para pausar no meio, e aí quem ainda não recebeu não recebe.`}
			</ConfirmationModal>
			<CampaignModal
				open={campaignModalOpen}
				onClose={handleCloseCampaignModal}
				campaignId={selectedCampaign?.id}
			/>
			<MainHeader>
				<Title>Campanhas</Title>
				<MainHeaderButtonsWrapper>
					<Button onClick={handleOpenCampaignModal}>
						<Plus className="h-4 w-4" />
						Nova campanha
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
				{/* Campanha é a tela que fala com muita gente de uma vez. */}
				<div className="mb-4 rounded-xl border bg-muted p-3 text-xs leading-relaxed">
					<p className="flex items-start gap-1.5 font-medium">
						<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
						Campanha manda a mesma mensagem — ou o mesmo fluxo — para vários
						clientes de uma vez, pelo seu WhatsApp.
					</p>
					<p className="mt-1.5 text-muted-foreground">
						Criar uma campanha <span className="font-medium text-foreground">
							não envia nada
						</span>: ela fica guardada como “Não enviada”. As mensagens só começam
						a sair quando alguém clica no botão de play (Iniciar) na linha dela —
						e o sistema pede confirmação antes.
					</p>
				</div>
				{/* Tabela: do tablet para cima. */}
				<div className="hidden rounded-xl border bg-card sm:block">
					<Table>
						<TableHeader className="sticky top-0 z-10 bg-card">
							<TableRow>
								<TableHead>Nome</TableHead>
								<TableHead>Sai por qual número</TableHead>
								{/* Só para quem enxerga a campanha dos outros: numa empresa
								    que divide carteira, "de quem é esta" é a primeira
								    pergunta de quem supervisiona. */}
								{vejoTodas && <TableHead>De quem é</TableHead>}
								<TableHead className="text-center">Situação</TableHead>
								<TableHead className="text-center">Quem já recebeu</TableHead>
								<TableHead className="text-center">Ações</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{campaigns.map((campaign) => (
								<TableRow key={campaign.id}>
									<TableCell className="font-medium">
										{campaign.name}
										{/* Campanha de fluxo não tem texto próprio: o que a
										    diferencia na lista é qual fluxo ela dispara. */}
										{campaign.flow && (
											<span className="mt-1 flex w-fit items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
												<Workflow className="h-3 w-3" />
												Fluxo: {campaign.flow.name}
											</span>
										)}
									</TableCell>
									<TableCell>
										{campaign.whatsapp?.name || "-"}
									</TableCell>
									{vejoTodas && (
										<TableCell className="text-muted-foreground">
											{campaign.creator?.name || "—"}
										</TableCell>
									)}
									<TableCell className="text-center">
										<CampaignStatusBadge status={campaign.status} />
										{STATUS_AJUDA[campaign.status] && (
											<span className="mx-auto mt-1 block max-w-[16rem] text-[11px] leading-snug text-muted-foreground">
												{STATUS_AJUDA[campaign.status]}
											</span>
										)}
									</TableCell>
									<TableCell className="text-center">
										{renderStats(campaign.id)}
									</TableCell>
									<TableCell>
										<TooltipProvider>
											<div className="flex items-center justify-center gap-1">
												{["draft", "paused", "scheduled"].includes(
													campaign.status
												) && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8 text-green-600 hover:text-green-600"
																onClick={() => setAIniciar(campaign)}
															>
																<Play className="h-4 w-4" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															Iniciar — começa a mandar as mensagens
														</TooltipContent>
													</Tooltip>
												)}
												{campaign.status === "running" && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8 text-yellow-600 hover:text-yellow-600"
																onClick={() =>
																	handleAction(
																		campaign.id,
																		"pause",
																		"Campanha pausada."
																	)
																}
															>
																<Pause className="h-4 w-4" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															Pausar — para de mandar agora
														</TooltipContent>
													</Tooltip>
												)}
												{["scheduled", "running", "paused"].includes(
													campaign.status
												) && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8"
																onClick={() =>
																	handleAction(
																		campaign.id,
																		"cancel",
																		"Campanha cancelada."
																	)
																}
															>
																<XCircle className="h-4 w-4" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															Cancelar — o resto dos contatos não recebe
														</TooltipContent>
													</Tooltip>
												)}
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8"
															onClick={() => abrirHistorico(campaign)}
														>
															<History className="h-4 w-4" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>
														Histórico — o que já aconteceu nesta campanha
													</TooltipContent>
												</Tooltip>
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8"
															onClick={() =>
																handleAction(
																	campaign.id,
																	"duplicate",
																	"Campanha duplicada."
																)
															}
														>
															<Copy className="h-4 w-4" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>
														Duplicar — cria uma cópia sem enviar nada
													</TooltipContent>
												</Tooltip>
												{["draft", "scheduled", "paused"].includes(
													campaign.status
												) && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8"
																onClick={() => handleEditCampaign(campaign)}
															>
																<Pencil className="h-4 w-4" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															Editar a mensagem e o número de saída
														</TooltipContent>
													</Tooltip>
												)}
												{["running", "paused", "completed"].includes(
													campaign.status
												) && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8"
																onClick={() => baixarRelatorio(campaign)}
															>
																<Download className="h-4 w-4" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															Baixar o relatório de entrega (CSV)
														</TooltipContent>
													</Tooltip>
												)}
												{campaign.status !== "running" && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8 text-destructive hover:text-destructive"
																onClick={() => {
																	setSelectedCampaign(campaign);
																	setConfirmModalOpen(true);
																}}
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															Apagar a campanha
														</TooltipContent>
													</Tooltip>
												)}
											</div>
										</TooltipProvider>
									</TableCell>
								</TableRow>
							))}
							{loading && <TableRowSkeleton columns={vejoTodas ? 6 : 5} />}
							{!loading && campaigns.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={vejoTodas ? 6 : 5}
										className="py-8 text-center text-sm text-muted-foreground"
									>
										Nenhuma campanha criada. Nada está sendo enviado.
										<span className="mt-1 block text-xs">
											Use “Nova campanha” para preparar um envio — ele fica
											parado até você mandar começar.
										</span>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>

				{/* Campanhas no celular.
				    Situação e progresso vêm no corpo do cartão: são o que se
				    abre esta tela para ver. As ações ficam numa faixa própria,
				    porque mudam conforme a situação — iniciar uma que já está
				    rodando não existe. */}
				<ul className="space-y-2 sm:hidden">
					{campaigns.map((campaign) => {
						const podeIniciar = ["draft", "paused", "scheduled"].includes(
							campaign.status
						);
						const podeCancelar = ["scheduled", "running", "paused"].includes(
							campaign.status
						);
						const podeEditar = ["draft", "scheduled", "paused"].includes(
							campaign.status
						);

						return (
							<li key={campaign.id} className="rounded-xl border bg-card">
								<div className="space-y-2 p-3">
									<div className="flex items-start justify-between gap-3">
										<span className="min-w-0 flex-1 truncate font-medium">
											{campaign.name}
										</span>
										<CampaignStatusBadge status={campaign.status} />
									</div>

									{STATUS_AJUDA[campaign.status] && (
										<p className="text-xs leading-snug text-muted-foreground">
											{STATUS_AJUDA[campaign.status]}
										</p>
									)}

									<p className="text-xs text-muted-foreground">
										Sai por: {campaign.whatsapp?.name || "—"}
									</p>

									{campaign.flow && (
										<span className="flex w-fit items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
											<Workflow className="h-3 w-3" />
											Fluxo: {campaign.flow.name}
										</span>
									)}

									<div className="text-sm">{renderStats(campaign.id)}</div>
								</div>

								<div className="flex items-center justify-end gap-1 border-t px-2 py-1.5">
									{podeIniciar && (
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9 text-green-600 hover:text-green-600"
											aria-label="Iniciar campanha"
											onClick={() => setAIniciar(campaign)}
										>
											<Play className="h-4 w-4" />
										</Button>
									)}
									{campaign.status === "running" && (
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9 text-yellow-600 hover:text-yellow-600"
											aria-label="Pausar campanha"
											onClick={() =>
												handleAction(campaign.id, "pause", "Campanha pausada.")
											}
										>
											<Pause className="h-4 w-4" />
										</Button>
									)}
									{podeCancelar && (
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9"
											aria-label="Cancelar campanha"
											onClick={() =>
												handleAction(
													campaign.id,
													"cancel",
													"Campanha cancelada."
												)
											}
										>
											<XCircle className="h-4 w-4" />
										</Button>
									)}
									<Button
										variant="ghost"
										size="icon"
										className="h-9 w-9"
										aria-label="Ver histórico"
										onClick={() => abrirHistorico(campaign)}
									>
										<History className="h-4 w-4" />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										className="h-9 w-9"
										aria-label="Duplicar campanha"
										onClick={() =>
											handleAction(
												campaign.id,
												"duplicate",
												"Campanha duplicada."
											)
										}
									>
										<Copy className="h-4 w-4" />
									</Button>
									{podeEditar && (
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9"
											aria-label="Editar campanha"
											onClick={() => handleEditCampaign(campaign)}
										>
											<Pencil className="h-4 w-4" />
										</Button>
									)}
									{campaign.status !== "running" && (
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9 text-destructive hover:text-destructive"
											aria-label="Apagar campanha"
											onClick={() => {
												setSelectedCampaign(campaign);
												setConfirmModalOpen(true);
											}}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									)}
								</div>
							</li>
						);
					})}

					{!loading && campaigns.length === 0 && (
						<li className="rounded-xl border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
							Nenhuma campanha ainda.
						</li>
					)}
					{loading && (
						<li className="rounded-xl border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
							Carregando...
						</li>
					)}
				</ul>
			</div>

			<Dialog
				open={!!historicoDe}
				onOpenChange={(o) => !o && setHistoricoDe(null)}
			>
				<DialogContent className="tema-campanha max-h-[80vh] overflow-y-auto sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>
							Histórico de “{historicoDe?.name}”
						</DialogTitle>
						<p className="text-xs text-muted-foreground">
							Cada início, pausa, repetição e resposta, do mais recente para o
							mais antigo.
						</p>
					</DialogHeader>
					{carregandoHistorico ? (
						<div className="flex justify-center py-8">
							<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
						</div>
					) : historico.length === 0 ? (
						<p className="py-8 text-center text-sm text-muted-foreground">
							Nada aconteceu ainda. O histórico começa no primeiro disparo.
						</p>
					) : (
						<ol className="space-y-2">
							{historico.map((linha) => (
								<li
									key={linha.id}
									className="rounded-lg border bg-card p-2.5 text-sm"
								>
									<div className="flex items-center justify-between gap-2">
										<Badge
											variant={
												linha.event === "error" ? "destructive" : "secondary"
											}
										>
											{ROTULO_DE_EVENTO[linha.event] || linha.event}
										</Badge>
										<span className="text-xs text-muted-foreground">
											{new Date(linha.createdAt).toLocaleString("pt-BR")}
										</span>
									</div>
									{linha.message && (
										<p className="pt-1.5 leading-relaxed text-muted-foreground">
											{linha.message}
										</p>
									)}
								</li>
							))}
						</ol>
					)}
				</DialogContent>
			</Dialog>

		</MainContainer>
	);
};

export default Campaigns;
